/**
 * useActivePaymentChannels — resolve the Payment Channel Master for a center.
 *
 * Companion of `useActiveChannels` (aggregator platforms). Returns the
 * enabled payment channels (Cash, Card, UPI, Other Online, Due) for the
 * given centre so Dashboard, Grid Update, Payment Breakdown, Daily Report,
 * Email Pack and Excel show ONLY the columns actually configured.
 *
 * Every screen rendering per-channel money figures MUST use this + the
 * `readPaymentValue` helper so the shape is consistent everywhere.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const _cache = new Map();

export function useActivePaymentChannels(session, center) {
  const [channels, setChannels] = useState(() => {
    const key = `${session?.token || ""}|${center || ""}`;
    return _cache.get(key) || [];
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!session?.token || !center) {
      setChannels([]);
      return;
    }
    const cacheKey = `${session.token}|${center}`;
    const cached = _cache.get(cacheKey);
    if (cached) {
      setChannels(cached);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await api.post(`/payment-channels/center/${center}`, {
          token: session.token,
        });
        const ch = res.data.channels || [];
        _cache.set(cacheKey, ch);
        if (!cancelled) setChannels(ch);
      } catch {
        if (!cancelled) setChannels([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.token, center]);

  return { channels, loading, hasKey: (k) => channels.some((c) => c.key === k) };
}

/** Read a payment channel's amount from a daily / summary doc. */
export function readPaymentValue(doc, channel, prefix = "") {
  if (!doc || !channel) return 0;
  const field = channel.field || channel.key;
  // Daily row uses the raw field (e.g. `card_idfc`), aggregated rows use a
  // `total_<field>` variant when the caller passes prefix="total_".
  const v =
    doc[`${prefix}${field}`] ??
    doc[`${prefix}${channel.key}`] ??
    doc[field];
  return Number(v) || 0;
}
